import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import MainDoctorCoverageComponent from "./components/MainDoctorCoverageComponent";
import MainSummaryComponent from "./components/MainSummaryComponent";
import { useSalesContext } from "../SalesContext";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import PartyTable from "./components/DashboardCard";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "defaultColor",
})<{ active: boolean; defaultColor: string }>(
  ({ theme, active, defaultColor }) => ({
    borderRadius: "12px",
    boxShadow: active
      ? "0 4px 12px rgba(0, 0, 0, 0.2)"
      : "0 4px 8px rgba(0, 0, 0, 0.1)",
    margin: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: !active ? "#ffffff" : defaultColor,
    cursor: "pointer",
    transition: "all 0.2s",
    width: "22%",
    minHeight: "100px",
    position: "relative",
    "&:hover": {
      opacity: 1,
      transform: active ? "none" : "scale(1.02)",
    },
  })
);

const SummaryStyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "defaultColor",
})<{ active: boolean; defaultColor: string }>(
  ({ theme, active, defaultColor }) => ({
    borderRadius: "12px",
    boxShadow: active
      ? "0 4px 12px rgba(0, 0, 0, 0.2)"
      : "0 4px 8px rgba(0, 0, 0, 0.1)",
    margin: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: !active ? "#ffffff" : defaultColor,

    cursor: active || defaultColor ? "pointer" : "default",
    transition: "all 0.2s",
    width: "22%",
    minHeight: "100px",
    position: "relative",
    "&:hover": {
      opacity: 1,
      transform: active ? "none" : "scale(1.02)",
    },
  })
);

const ValueTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ theme, active }) => ({
  fontSize: "1.5rem",
  fontWeight: "bold",
  color: active ? "#ffffff" : "#000000",
}));

const LabelTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ theme, active }) => ({
  fontSize: "12px",
  color: active ? "#ffffff" : "#000000",
}));

interface CardData {
  value?: string | number;
  label?: string;
  color?: string;
  defaultColor?: string;
  icon?: React.ReactNode;
}
interface DecodedToken {
  employeeID: string;
  name: string;
  preferred_username: string;
}

const PlaceholderComponent: React.FC = () => <Box></Box>;
const saveTokenWithExpiry = (token: string, expiryDays: number = 1) => {
  const now = new Date().getTime();
  const expiry = now + expiryDays * 24 * 60 * 60 * 1000;
  const tokenData = {
    value: token,
    expiry,
  };
  localStorage.setItem("access_token", JSON.stringify(tokenData));
};

const getValidToken = (): string | null => {
  const item = localStorage.getItem("access_token");
  if (!item) return null;

  try {
    const { value, expiry } = JSON.parse(item);
    const now = new Date().getTime();
    if (now > expiry) {
      localStorage.removeItem("access_token");
      return null;
    }
    return value;
  } catch (e) {
    console.error("Error parsing token from storage:", e);
    localStorage.removeItem("access_token");
    return null;
  }
};

const MyPerformance: React.FC = () => {
  const { kpiData, showatable, setShowtable } = useSalesContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryToken = queryParams.get("access_token");

    if (queryToken) {
      saveTokenWithExpiry(queryToken);
      navigate("/app/my_performance", { replace: true });
    }
  }, [location, navigate]);
  const token = getValidToken();
  let userName = "";
  let user_id = "";

  if (token) {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      userName = decoded?.name || "";
      user_id = decoded?.employeeID || "";
    } catch (e) {
      console.error("Invalid token", e);
    }
  }

  const summaryCoverageData = kpiData?.summaryDoctorCoverageCard || {
    actual: 0,
    total: 0,
  };

  const jsonData = kpiData?.doctorCoverageCard || { actual: 0, total: 0 };

  const defaultCards: CardData[] = [
    {
      value: `${jsonData.actual}/${jsonData.total}`,
      label: "Doctor Coverage",
      color: "#fff",
      defaultColor: "#297F9D",
    },
  ];

  const summaryCards: CardData[] = [
    {
      value: `${summaryCoverageData.actual}/${summaryCoverageData.total}`,
      label: "Doctor Coverage",
      color: "#fff",
      defaultColor: "#297F9D",
    },
  ];

  const [activeCardIndex, setActiveCardIndex] = useState<number>(0);
  const [isSummaryView, setIsSummaryView] = useState<boolean>(false);

  const handleSummaryClick = () => {
    setIsSummaryView(true);
    setActiveCardIndex(0);
  };

  const handleBackClick = () => {
    setIsSummaryView(false);
    setActiveCardIndex(0);
    setShowtable(false);
  };

  const renderActiveComponent = () => {
    if (isSummaryView) {
      switch (activeCardIndex) {
        case 0:
          return <MainSummaryComponent user_id={user_id} />;
        default:
          return <PlaceholderComponent />;
      }
    } else {
      switch (activeCardIndex) {
        case 0:
          return <MainDoctorCoverageComponent user_id={user_id} />;
        default:
          return <PlaceholderComponent />;
      }
    }
  };
  if (showatable) {
    return (
      <Box
        sx={{
          margin: "10px auto",
          position: "relative",
          overflow: "visible",
          width: "97%",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
        >
          <Typography
            variant="body2"
            color="#3B459E"
            sx={{ fontWeight: "700", cursor: "pointer" }}
            onClick={handleBackClick}
          >
            Back
          </Typography>
        </Box>
        <PartyTable />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        margin: "24px auto",
        backgroundColor: "#DDE8EB",
        borderRadius: "20px 20px 0px 0px",
        height: "155px",
        position: "relative",
        overflow: "visible",
        width: "97%",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        p={2}
      >
        <Typography variant="h6" fontWeight="bold">
          {isSummaryView
            ? "My Performance: Summary"
            : "My Performance: Doctor Coverage"}
        </Typography>
        <Typography
          variant="body2"
          color="#3B459E"
          sx={{ fontWeight: "700", cursor: "pointer" }}
          onClick={isSummaryView ? handleBackClick : handleSummaryClick}
        >
          {isSummaryView ? "Back" : "Summary"}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="#FD1D1D"
        sx={{ fontWeight: "700", padding: "0 16px" }}
      >
        {user_id} - {userName} | (Mankind)
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        sx={{
          position: "relative",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1,
          padding: "0 16px",
        }}
      >
        {(isSummaryView ? summaryCards : defaultCards).map((card, index) => (
          <Box
            key={index}
            component={isSummaryView ? SummaryStyledCard : StyledCard}
            active={activeCardIndex === index}
            defaultColor={card.defaultColor || "#ffffff"}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                height: "100%",
              }}
            >
              {card.icon && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    backgroundColor: card.defaultColor || "#ffffff",
                    borderRadius: "4px",
                    width: "40px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {card.icon}
                </Box>
              )}
              <Box sx={{ textAlign: "center" }}>
                {card.value && card.label ? (
                  <>
                    <ValueTypography active={activeCardIndex === index}>
                      {card.value}
                    </ValueTypography>
                    <LabelTypography active={activeCardIndex === index}>
                      {card.label}
                    </LabelTypography>
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  ></Typography>
                )}
              </Box>
            </CardContent>
          </Box>
        ))}
      </Box>
      <Box p={0} sx={{ width: "100%" }}>
        {" "}
        {renderActiveComponent()}
      </Box>
    </Box>
  );
};

export default MyPerformance;
